import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import Layout from '../../../brands/components/Layout'
import DataGrid from 'react-data-grid'
import { Icon } from '@iconify/react'
import { useTranslation } from 'react-i18next'
import { InputField } from '../../../../components'
import { useAppDispatch } from '../../../../redux/hook'
import {
    setLeavePolicyDetails,
    updateLeavePoliciesDetails,
} from '../slice/index'
import { updateLeavePolicyDetails, getLeavePolicyDetails } from '../api/index'
import { UpdateLeavePolicies } from '../../api/index'
import { LeavePolicyDetails, LeavePolicyTitleUpdate } from '../slice/types'
import { HIDE_LOADER, SHOW_LOADER } from '../../../../redux/slices/LoaderSlice'
import { SHOW_NOTIFICATION } from '../../../../redux/slices/showNotificationSlice'
import { UpdatedRow, Row } from '../types'
import './index.css'

const EditPolicy: React.FC = () => {
    const [toggle, setToggle] = React.useState<boolean>(false)
    const [rows, setRows] = useState<any[]>([])
    const [policyName, setPolicyName] = useState<string>('')
    const [startDate, setStartDate] = useState<string>('')
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const location = useLocation()
    const policyDetails = location?.state?.toUpdatePolicyDetails
    const pathname = location.pathname
    const id = pathname.substring(pathname.lastIndexOf('/') + 1)

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch(SHOW_LOADER())
                const response = await getLeavePolicyDetails(id)
                setPolicyName(response?.data?.title)
                setStartDate(response?.data?.leavePolicyStartDate)
                setRows(
                    response?.data?.leavePolicyDetails.map((detail: any) => ({
                        type: detail.leaveTypeTitle,
                        leaves: detail.isUnlimited
                            ? '0'
                            : detail.numberOfLeaves,
                        carryover: detail.isCarryOver ? 'Yes' : 'No',
                        unlimited: detail.isUnlimited ? 'Yes' : 'No',
                        prorata: detail.isProRata ? 'Yes' : 'No',
                        editable: false,
                        id: detail.id,
                    }))
                )
                dispatch(
                    setLeavePolicyDetails(
                        response?.data?.leavePolicyDetails.map(
                            (detail: any) => ({
                                type: detail.leaveTypeTitle,
                                leaves: detail.isUnlimited
                                    ? '0'
                                    : detail.numberOfLeaves,
                                carryover: detail.isCarryOver ? 'Yes' : 'No',
                                unlimited: detail.isUnlimited ? 'Yes' : 'No',
                                prorata: detail.isProRata ? 'Yes' : 'No',
                                editable: false,
                                id: detail.id,
                            })
                        )
                    )
                )
            } catch (error) {
                console.error('Error fetching leave types:', error)
            } finally {
                dispatch(HIDE_LOADER())
            }
        }

        fetchData()
    }, [policyDetails])

    const makeEditable = (index: number) => {
        setRows(
            rows.map((row, i) => {
                if (i === index) {
                    return { ...row, editable: !row.editable } // Set editable to true for the item at the specified index
                }
                return row
            })
        )
    }

    const handleRowUpdate = async (updatedRow: UpdatedRow, index: number) => {
        const numberOfLeaves = parseInt(updatedRow.leaves, 10)
        try {
            const { id, carryover, unlimited, prorata } = updatedRow

            const requestBody: LeavePolicyDetails = {
                id,
                numberOfLeaves,
                isCarryOver: carryover === 'Yes' ? true : false,
                isProRata: prorata === 'Yes' ? true : false,
                isUnlimited: unlimited === 'Yes' ? true : false,
            }
            const response = await updateLeavePolicyDetails(requestBody)
            if (response?.status === 200) {
                dispatch(updateLeavePoliciesDetails(requestBody))
                dispatch(
                    SHOW_NOTIFICATION({
                        message: 'Leave Policy detail updated successfully',
                        type: 'success',
                    })
                )
            }
        } catch (error) {
            console.error(error)
        } finally {
            makeEditable(index)
        }
    }

    const handleInputChange = (index: number, key: string, value: string) => {
        setRows((prevRows) =>
            prevRows.map((row, i) => {
                if (i === index) {
                    return { ...row, [key]: value } // Update the specified key with the new value
                }
                return row
            })
        )
    }

    const handlePolicyNameUpdate = async () => {
        try {
            const requestBody: LeavePolicyTitleUpdate = {
                id: id,
                title: policyName,
                leavePolicyStartDate: startDate,
            }
            await UpdateLeavePolicies(requestBody)
        } catch (error) {
            console.error(error)
        } finally {
            setToggle(false)
        }
    }

    const handleCheckBoxChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        row: Row
    ) => {
        const name = e.target.name as keyof Row

        const updatedRows = rows.map((item: any) => {
            if (item.id === row.id) {
                return { ...item, [name]: row[name] === 'Yes' ? 'No' : 'Yes' }
            } else {
                return item
            }
        })
        setRows(updatedRows)
    }

    const column = [
        {
            key: 'type',
            name: `Leave Type`,
            resizable: true,
            sortable: true,
            renderCell: (props: any) => {
                return <div>{props.row.type}</div>
            },
        },
        {
            key: 'leaves',
            name: `Max Number of Leaves`,
            resizable: true,
            sortable: true,
            renderCell: (props: any) => {
                return !props.row.editable ? (
                    <div>{props.row.leaves}</div>
                ) : (
                    <div className="d-flex align-items-center bg-green-color">
                        <input
                            type="text"
                            onChange={(e) =>
                                handleInputChange(
                                    props.rowIdx,
                                    'leaves',
                                    e.target.value
                                )
                            }
                            className="form-control"
                            placeholder={props.row.leaves}
                            value={props.row.leaves}
                        />
                    </div>
                )
            },
        },
        {
            key: 'carryover',
            name: `Carryover`,
            resizable: true,
            sortable: true,
            renderCell: (props: any) => {
                return !props.row.editable ? (
                    <div>{props.row.carryover}</div>
                ) : (
                    <div className="test grid-checks">
                        <input
                            name="carryover"
                            type="checkbox"
                            id="emp-selection"
                            onChange={(e) => handleCheckBoxChange(e, props.row)}
                            checked={props.row.carryover === 'Yes'}
                        />
                        <label
                            className="sb-checkbox__label"
                            htmlFor="emp-selection"
                        ></label>
                    </div>
                )
            },
        },
        {
            key: 'unlimited',
            name: `Unlimited`,
            resizable: true,
            sortable: true,
            renderCell: (props: any) => {
                return !props.row.editable ? (
                    <div>{props.row.unlimited}</div>
                ) : (
                    <div className="test grid-checks">
                        <input
                            name="unlimited"
                            type="checkbox"
                            id="emp-selection"
                            onChange={(e) => handleCheckBoxChange(e, props.row)}
                            checked={props.row.unlimited === 'Yes'}
                        />
                        <label
                            className="sb-checkbox__label"
                            htmlFor="emp-selection"
                        ></label>
                    </div>
                )
            },
        },
        {
            key: 'prorata',
            name: `Prorata`,
            resizable: true,
            sortable: true,
            renderCell: (props: any) => {
                return !props.row.editable ? (
                    <div>{props.row.prorata}</div>
                ) : (
                    <div className="test">
                        <input
                            type="checkbox"
                            name="prorata"
                            onChange={(e) => handleCheckBoxChange(e, props.row)}
                            checked={props.row.prorata === 'Yes'}
                        />
                    </div>
                )
            },
        },
        {
            key: '',
            name: `${t('brand.tableColumns.action')}`, // Fix the typo here
            resizable: true,
            renderCell: (props: any) => {
                return !props.row.editable ? (
                    <div className="dropdown-custom-link">
                        <Icon
                            icon="ion:edit"
                            color="#39877C"
                            cursor={'pointer'}
                            onClick={() => makeEditable(props.rowIdx)}
                        />
                    </div>
                ) : (
                    <div className="dropdown-custom-link">
                        <Icon
                            icon="carbon:save"
                            className="icon-primary"
                            color="#39877C"
                            cursor={'pointer'}
                            onClick={() =>
                                handleRowUpdate(props.row, props.rowIdx)
                            }
                        />
                    </div>
                )
            },
        },
    ]

    return (
        <div>
            <Layout>
                <div className="col-12 grid-custom-table">
                    <div className="page-information mb-3">
                        <div className="row">
                            <div className="col-sm-6">
                                <h4>
                                    {!toggle ? (
                                        <>
                                            <span>
                                                {policyName}
                                                <span
                                                    className="ms-2"
                                                    onClick={() => {
                                                        setToggle(true)
                                                    }}
                                                >
                                                    <Icon
                                                        icon="ion:edit"
                                                        color="#39877C"
                                                        height={'18px'}
                                                        width={'18px'}
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                </span>
                                            </span>
                                        </>
                                    ) : (
                                        <div className="col-sm-8 d-flex align-items-baseline bg-green-color">
                                            <InputField
                                                type="text"
                                                name="policyName"
                                                value={policyName}
                                                placeholder="Standard First-Year Policy"
                                                onChange={(e) =>
                                                    setPolicyName(
                                                        e.target.value
                                                    )
                                                }
                                                onBlur={() => {
                                                    return
                                                }}
                                                error={''}
                                                touched={true}
                                            />
                                            <span className="ms-2">
                                                <a
                                                    onClick={() =>
                                                        handlePolicyNameUpdate()
                                                    }
                                                    className="button button-primary button-align-right"
                                                >
                                                    SAVE
                                                </a>
                                            </span>
                                        </div>
                                    )}
                                </h4>
                            </div>
                        </div>
                    </div>
                    <div className="grid-table-timeoff">
                        <DataGrid
                            className="data-grid-table "
                            columns={column}
                            rows={rows}
                            rowHeight={60}
                            headerRowHeight={40}
                        />
                    </div>
                </div>
            </Layout>
        </div>
    )
}

export default EditPolicy
